import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Mail, MessageSquare, PhoneCall, Sparkles, Send, Zap } from "lucide-react";
import api, { errMessage } from "../lib/api";

const CHANNELS = [
  { id: "email", label: "Email", Icon: Mail },
  { id: "sms", label: "SMS", Icon: MessageSquare },
  { id: "voice", label: "AI Call", Icon: PhoneCall },
];

export default function OutreachPanel({ property, onClose }) {
  const agent = property.listing_agent || {};
  const [channel, setChannel] = useState("email");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [sequences, setSequences] = useState([]);
  const [selSequence, setSelSequence] = useState("");
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    // Load the user's sequences once — shown on SMS tab
    api.get("/sms/sequences").then((r) => setSequences(r.data || [])).catch(() => {});
  }, []);

  // Prefill recipient when channel changes
  useEffect(() => {
    if (channel === "email") setTo(agent.email || "");
    else setTo(agent.phone || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  const draft = async () => {
    setDrafting(true);
    try {
      const r = await api.post("/outreach/draft", {
        property_data: property,
        channel,
      });
      if (channel === "email") {
        setSubject(r.data.subject || `Interested in ${property.address}`);
      }
      setBody(r.data.body || "");
      toast.success("AI draft ready — edit before sending");
    } catch (e) {
      toast.error(errMessage(e, "Draft failed"));
    } finally {
      setDrafting(false);
    }
  };

  const send = async () => {
    if (!to.trim() || !body.trim()) {
      toast.error("Fill in recipient and message");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/outreach/send", {
        channel,
        property_data: property,
        to: to.trim(),
        subject: subject || undefined,
        body,
      });
      const d = r.data;
      if (d.sent) {
        toast.success(
          channel === "voice"
            ? `AI call queued (Bland ID: ${d.provider_id || "—"})`
            : `${channel.toUpperCase()} sent`
        );
        onClose();
      } else if (d.provider_status === "no_key") {
        toast.error("Bland.ai key not configured — add BLAND_API_KEY and retry");
      } else {
        toast.error(d.provider_error || "Send failed");
      }
    } catch (e) {
      toast.error(errMessage(e, "Send failed"));
    } finally {
      setSending(false);
    }
  };

  const startSequence = async () => {
    if (!selSequence) {
      toast.error("Pick a sequence first");
      return;
    }
    if (!to.trim()) {
      toast.error("Enter recipient phone");
      return;
    }
    setEnrolling(true);
    try {
      const r = await api.post("/sms/enrollments", {
        sequence_id: selSequence,
        contact_phone: to.trim(),
        contact_name: agent.name || "",
        property_data: property,
      });
      toast.success(`Enrolled — step 1 of ${r.data.current_step ? "multi" : "1"} sent`);
      onClose();
    } catch (e) {
      toast.error(errMessage(e, "Enroll failed"));
    } finally {
      setEnrolling(false);
    }
  };

  const ChannelIcon = CHANNELS.find((c) => c.id === channel).Icon;

  return (
    <div
      data-testid="outreach-panel"
      className="fixed inset-0 z-[55] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-xl w-full max-h-[92vh] overflow-y-auto border border-[--ink] animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[--line] flex items-center justify-between">
          <div>
            <div className="overline">// Outreach</div>
            <h2 className="font-display text-xl font-bold tracking-tighter mt-1">
              {property.address}
            </h2>
            {agent.name && (
              <div className="text-xs font-mono text-[--ink-muted] mt-0.5">
                Agent: {agent.name}
                {agent.phone ? ` · ${agent.phone}` : ""}
                {agent.email ? ` · ${agent.email}` : ""}
              </div>
            )}
            {!agent.name && (
              <div className="text-xs font-mono text-[--ink-muted] mt-0.5">
                No agent on file — enter recipient manually
              </div>
            )}
          </div>
          <button
            data-testid="close-outreach"
            onClick={onClose}
            className="p-2 hover:bg-[--bg-soft] border border-[--line]"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-1 border border-[--line] p-1 bg-[--bg-soft]">
            {CHANNELS.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`channel-${c.id}`}
                onClick={() => setChannel(c.id)}
                className={`flex items-center justify-center gap-1.5 py-2 text-xs font-medium ${
                  channel === c.id ? "bg-[--ink] text-white" : "text-[--ink-soft] hover:text-[--ink]"
                }`}
              >
                <c.Icon size={12} strokeWidth={1.5} /> {c.label}
              </button>
            ))}
          </div>

          <label className="block">
            <span className="text-xs text-[--ink-muted] block mb-1 font-mono uppercase tracking-wider">
              {channel === "email" ? "To (email)" : "To (phone E.164 e.g. +19705551234)"}
            </span>
            <input
              data-testid="outreach-to"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full text-sm font-mono"
              placeholder={channel === "email" ? "agent@example.com" : "+19705551234"}
            />
          </label>

          {channel === "email" && (
            <label className="block">
              <span className="text-xs text-[--ink-muted] block mb-1 font-mono uppercase tracking-wider">
                Subject
              </span>
              <input
                data-testid="outreach-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full text-sm"
              />
            </label>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[--ink-muted] font-mono uppercase tracking-wider">
                {channel === "voice" ? "Call script (what the AI will say)" : "Message"}
              </span>
              <button
                type="button"
                data-testid="outreach-draft"
                onClick={draft}
                disabled={drafting}
                className="flex items-center gap-1 text-xs text-[--accent] hover:underline"
              >
                <Sparkles size={11} strokeWidth={1.5} />
                {drafting ? "Drafting…" : body ? "Redraft with AI" : "Draft with AI"}
              </button>
            </div>
            <textarea
              data-testid="outreach-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={channel === "voice" ? 12 : 8}
              className="w-full text-sm"
              placeholder={
                channel === "voice"
                  ? "Click 'Draft with AI' to generate the call script, or write your own…"
                  : "Click 'Draft with AI' for a tailored message, or write your own…"
              }
            />
          </div>

          {channel === "voice" && (
            <div className="text-[11px] font-mono text-[--ink-muted] border-l-2 border-[--accent] pl-3">
              AI phone call via Bland.ai · ~$0.09–$0.14/min · call recording + transcript available after completion.
              {!body && " Draft first to get started."}
            </div>
          )}

          {channel === "sms" && sequences.length > 0 && (
            <div className="border-t border-[--line] pt-4" data-testid="outreach-seq-block">
              <div className="overline mb-2 flex items-center gap-1.5">
                <Zap size={11} strokeWidth={1.5} /> Or start a follow-up sequence
              </div>
              <div className="flex gap-2">
                <select
                  data-testid="outreach-seq-select"
                  value={selSequence}
                  onChange={(e) => setSelSequence(e.target.value)}
                  className="flex-1 text-sm"
                >
                  <option value="">— pick sequence —</option>
                  {sequences.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} · {s.steps.length} step{s.steps.length !== 1 ? "s" : ""}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  data-testid="outreach-seq-enroll"
                  onClick={startSequence}
                  disabled={!selSequence || enrolling || !to.trim()}
                  className="btn-ghost text-xs disabled:opacity-40 whitespace-nowrap"
                >
                  {enrolling ? "Enrolling…" : "Enroll →"}
                </button>
              </div>
              <div className="text-[10px] text-[--ink-muted] mt-1.5 font-mono">
                Step 1 fires immediately; remaining steps auto-send on schedule. Replies pause the drip.
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-[--line] p-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">
            Cancel
          </button>
          <button
            type="button"
            data-testid="outreach-send"
            onClick={send}
            disabled={sending || !to.trim() || !body.trim()}
            className="btn-primary flex items-center gap-2"
          >
            {channel === "voice" ? (
              <PhoneCall size={14} strokeWidth={1.5} />
            ) : (
              <Send size={14} strokeWidth={1.5} />
            )}
            {sending
              ? "Sending…"
              : channel === "voice"
              ? "Place AI call"
              : channel === "email"
              ? "Send email"
              : "Send SMS"}
          </button>
        </div>
      </div>
    </div>
  );
}
